import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  TrashIcon,
  CubeIcon,
  GiftIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline'
import { Factory, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { tipologieBoxApi, configurazioniApi } from '@/api/box'
import { giacenzeBoxApi } from '@/api/magazzino'
import { fetchAllPaginatedResults } from '@/utils/pagination'
import {
  STOCK_FILTER_OPTIONS,
  STOCK_FILTER_VALUES,
} from '@/utils/stockFilters'

/**
 * Pagina lista tipologie box.
 * Mostra tutte le box vendibili con filtri e azioni CRUD.
 */
export default function BoxList() {
  const [search, setSearch] = useState('')
  const [filtroAttivo, setFiltroAttivo] = useState('tutti')
  const [filtroEcommerce, setFiltroEcommerce] = useState('tutti')
  const [stockFilter, setStockFilter] = useState(STOCK_FILTER_VALUES.ALL)
  const [valoreMetrica, setValoreMetrica] = useState('costo')
  const [ordering, setOrdering] = useSortingState('box-tipologie', 'nome')
  const [deleteDialog, setDeleteDialog] = useState({ open: false, box: null })
  const [duplicaDialog, setDuplicaDialog] = useState({ open: false, box: null, nuovoCodice: '', nuovoNome: '' })
  const [stockDialog, setStockDialog] = useState({ open: false, box: null, giacenza: null })
  const [stockForm, setStockForm] = useState({ ubicazione: '' })
  
  const queryClient = useQueryClient()

  // Query tipologie box
  const { page, setPage } = useListPagination({ search, filtroAttivo, filtroEcommerce, stockFilter, ordering })

  const { data: boxData, isLoading, isFetching, error } = useQuery({
    queryKey: ['tipologie-box', { page, stockFilter, search, attivo: filtroAttivo, ecommerce: filtroEcommerce, ordering }],
    queryFn: () => {
      const params = { ordering, page, stock_filter: stockFilter }
      if (search) params.search = search
      if (filtroAttivo !== 'tutti') params.attivo = filtroAttivo === 'attive'
      if (filtroEcommerce !== 'tutti') params.disponibile_ecommerce = filtroEcommerce === 'si'
      return tipologieBoxApi.getAll(params)
    },
  })

  // Query configurazioni per filtro
  const { data: configurazioniData } = useQuery({
    queryKey: ['configurazioni-scatola'],
    queryFn: () => configurazioniApi.getAll({ attivo: true }),
  })

  // Query giacenze box (tutte le pagine) per mostrare stock insieme alla lista tipologie
  const { data: giacenzeData } = useQuery({
    queryKey: ['giacenze-box', 'catalogo-all-pages'],
    queryFn: () => fetchAllPaginatedResults((params) => giacenzeBoxApi.getAll(params)),
  })

  // Query KPI valore vista corrente box (allineato ai filtri lista)
  const [lastValidValoreVista, setLastValidValoreVista] = useState(null)
  const {
    data: valoreVistaData,
    isFetching: isValoreVistaFetching,
    isError: isValoreVistaError,
    refetch: refetchValoreVista,
  } = useQuery({
    queryKey: [
      'giacenze-box',
      'valore-vista',
      { search, attivo: filtroAttivo, ecommerce: filtroEcommerce, stockFilter, metrica: valoreMetrica, ordering },
    ],
    queryFn: () => {
      const params = {
        metrica: valoreMetrica,
        ordering,
      }
      if (search) params.search = search
      if (filtroAttivo !== 'tutti') params.attivo = filtroAttivo === 'attive'
      if (filtroEcommerce !== 'tutti') params.disponibile_ecommerce = filtroEcommerce === 'si'
      if (stockFilter !== STOCK_FILTER_VALUES.ALL) params.stock_filter = stockFilter
      return giacenzeBoxApi.valoreVista(params)
    },
  })

  // Mutation eliminazione
  const deleteMutation = useMutation({
    mutationFn: tipologieBoxApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipologie-box'] })
      toast.success('Box eliminata con successo')
      setDeleteDialog({ open: false, box: null })
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione')
    },
  })

  // Mutation duplicazione
  const duplicaMutation = useMutation({
    mutationFn: ({ id, data }) => tipologieBoxApi.duplica(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipologie-box'] })
      toast.success('Box duplicata con successo')
      setDuplicaDialog({ open: false, box: null, nuovoCodice: '', nuovoNome: '' })
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.error || 'Errore durante la duplicazione'
      toast.error(errorMsg)
    },
  })

  // Mutation modifica giacenza box inline
  const updateStockMutation = useMutation({
    mutationFn: ({ giacenzaId, data }) => giacenzeBoxApi.patch(giacenzaId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['giacenze-box'] })
      toast.success('Giacenza box aggiornata con successo')
      setStockDialog({ open: false, box: null, giacenza: null })
    },
    onError: (error) => {
      const errorData = error.response?.data
      const firstError = errorData ? Object.values(errorData)[0] : null
      toast.error(Array.isArray(firstError) ? firstError[0] : (firstError || 'Errore durante l\'aggiornamento giacenza'))
    },
  })

  const tipologie = Array.isArray(boxData?.data)
    ? boxData.data
    : (boxData?.data?.results || [])

  const configurazioni = Array.isArray(configurazioniData?.data)
    ? configurazioniData.data
    : (configurazioniData?.data?.results || [])
  const totaleConfigurazioni = typeof configurazioniData?.data?.count === 'number'
    ? configurazioniData.data.count
    : configurazioni.length
  const giacenze = Array.isArray(giacenzeData) ? giacenzeData : []
  const giacenzeByTipologia = new Map(
    giacenze.map((g) => [g.tipologia, g])
  )
  const valoreVistaApi = valoreVistaData?.data
  useEffect(() => {
    if (valoreVistaApi && typeof valoreVistaApi.count === 'number') {
      setLastValidValoreVista(valoreVistaApi)
    }
  }, [valoreVistaApi])
  const valoreVista = valoreVistaApi || lastValidValoreVista
  const quantitaVista = valoreVista?.quantita || { disponibile: 0, riservata: 0, effettiva: 0 }
  const valoreVistaCents = valoreVista?.valore_cents || { disponibile: 0, riservata: 0, effettiva: 0 }
  const isValoreVistaEmpty = (valoreVista?.count ?? 0) === 0
  const tipologieFiltrate = tipologie
  const hasActiveFilters = Boolean(
    search || filtroAttivo !== 'tutti' || filtroEcommerce !== 'tutti' || stockFilter !== STOCK_FILTER_VALUES.ALL
  )

  const confirmDelete = (box) => {
    setDeleteDialog({ open: true, box })
  }

  const handleDelete = () => {
    if (deleteDialog.box) {
      deleteMutation.mutate(deleteDialog.box.id)
    }
  }

  const openDuplicaDialog = (box) => {
    setDuplicaDialog({
      open: true,
      box,
      nuovoCodice: `${box.codice}-COPIA`,
      nuovoNome: `${box.nome} (Copia)`,
    })
  }

  const handleDuplica = () => {
    if (duplicaDialog.box && duplicaDialog.nuovoCodice.trim()) {
      duplicaMutation.mutate({
        id: duplicaDialog.box.id,
        data: {
          nuovo_codice: duplicaDialog.nuovoCodice.trim(),
          nuovo_nome: duplicaDialog.nuovoNome.trim() || undefined,
        },
      })
    }
  }

  const openStockDialog = (box) => {
    const giacenza = giacenzeByTipologia.get(box.id)
    if (!giacenza) {
      toast.error('Per questa box non è presente una giacenza da modificare')
      return
    }

    setStockForm({
      ubicazione: giacenza.ubicazione || '',
    })
    setStockDialog({ open: true, box, giacenza })
  }

  const handleStockSubmit = () => {
    if (!stockDialog.giacenza) return

    updateStockMutation.mutate({
      giacenzaId: stockDialog.giacenza.id,
      data: {
        ubicazione: stockForm.ubicazione.trim(),
      },
    })
  }

  const handleResetFilters = () => {
    setSearch('')
    setFiltroAttivo('tutti')
    setFiltroEcommerce('tutti')
    setStockFilter(STOCK_FILTER_VALUES.ALL)
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatCurrencyFromCents = (cents = 0) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format((cents || 0) / 100)
  }

  const getStockBadge = (giacenza) => {
    if (!giacenza) return <Badge variant="secondary">N/D</Badge>
    const effettiva = giacenza.quantita_effettiva ?? (giacenza.quantita_disponibile - giacenza.quantita_riservata)
    if (effettiva <= 0) return <Badge variant="destructive">Esaurito</Badge>
    if (effettiva <= 5) return <Badge variant="warning">Basso</Badge>
    return <Badge variant="success">OK</Badge>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tipologie Box</h1>
          <p className="text-muted-foreground">
            Gestisci le box vendibili con composizione prodotti
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/box/tipologie/assembla">
              <Factory className="h-4 w-4 mr-2" />
              Assembla Box
            </Link>
          </Button>
          <Button asChild>
            <Link to="/box/tipologie/nuovo">
              <PlusIcon className="h-4 w-4 mr-2" />
              Nuova Box
            </Link>
          </Button>
        </div>
      </div>

      {/* Statistiche rapide */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale Box</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tipologie.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Attive</CardTitle>
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {tipologie.filter(b => b.attivo).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">E-commerce</CardTitle>
            <GiftIcon className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {tipologie.filter(b => b.disponibile_ecommerce).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Configurazioni</CardTitle>
            <CubeIcon className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {totaleConfigurazioni}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome o codice..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroAttivo} onValueChange={setFiltroAttivo}>
              <SelectTrigger>
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="attive">Attive</SelectItem>
                <SelectItem value="inattive">Inattive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroEcommerce} onValueChange={setFiltroEcommerce}>
              <SelectTrigger>
                <SelectValue placeholder="E-commerce" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="si">Su E-commerce</SelectItem>
                <SelectItem value="no">Non su E-commerce</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Stock" />
              </SelectTrigger>
              <SelectContent>
                {STOCK_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="md:col-span-5 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                disabled={!hasActiveFilters}
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reset filtri
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-base font-medium">
              Valore economico del magazzino (Box assemblate)
            </CardTitle>
            <div className="w-full md:w-[220px]">
              <Select value={valoreMetrica} onValueChange={setValoreMetrica}>
                <SelectTrigger>
                  <SelectValue placeholder="Metrica valore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="costo">Al costo di composizione</SelectItem>
                  <SelectItem value="vendita">Al prezzo di vendita</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valore fisicamente disponibile</p>
              {isValoreVistaFetching ? (
                <div className="mt-2 h-12 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold text-emerald-600">{formatCurrencyFromCents(valoreVistaCents.disponibile)}</p>
                  <p className="text-sm text-muted-foreground">{quantitaVista.disponibile} unità complessive</p>
                </>
              )}
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valore impegnato negli ordini</p>
              {isValoreVistaFetching ? (
                <div className="mt-2 h-12 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold text-amber-600">{formatCurrencyFromCents(valoreVistaCents.riservata)}</p>
                  <p className="text-sm text-muted-foreground">{quantitaVista.riservata} unità riservate</p>
                </>
              )}
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Valore libero per nuovi ordini</p>
              {isValoreVistaFetching ? (
                <div className="mt-2 h-12 animate-pulse rounded bg-muted" />
              ) : (
                <>
                  <p className="mt-1 text-2xl font-semibold text-primary">{formatCurrencyFromCents(valoreVistaCents.effettiva)}</p>
                  <p className="text-sm text-muted-foreground">{quantitaVista.effettiva} unità libere</p>
                </>
              )}
            </div>
          </div>
          {!isValoreVistaError && !isValoreVistaFetching && isValoreVistaEmpty && (
            <p className="text-xs text-muted-foreground">
              Nessun elemento nella vista corrente: KPI valorizzati a zero.
            </p>
          )}
          {!isValoreVistaError && !isValoreVistaFetching && !isValoreVistaEmpty && (
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{valoreVista?.count ?? 0}</span> referenze considerate · valori {valoreMetrica === 'costo' ? 'al costo di composizione' : 'al prezzo di vendita'}
            </p>
          )}
          {isValoreVistaError && (
            <div className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-destructive">
                KPI temporaneamente non disponibili. La tabella box resta operativa.
                {lastValidValoreVista ? ' Sono mostrati gli ultimi valori disponibili.' : ''}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => refetchValoreVista()}>
                Riprova KPI
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabella Box */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : tipologieFiltrate.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna box trovata</p>
              <p className="text-sm mt-1">
                {hasActiveFilters ? 'Prova a modificare i filtri di ricerca' : 'Inizia creando la prima box'}
              </p>
              {!hasActiveFilters && (
              <Button asChild className="mt-4" variant="outline">
                <Link to="/box/tipologie/nuovo">Crea la prima box</Link>
              </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Immagine</TableHead>
                  <SortableTableHead field="codice" currentOrdering={ordering} onSort={setOrdering}>
                    Codice
                  </SortableTableHead>
                  <SortableTableHead field="nome" currentOrdering={ordering} onSort={setOrdering}>
                    Nome
                  </SortableTableHead>
                  <SortableTableHead field="configurazione__nome" currentOrdering={ordering} onSort={setOrdering}>Configurazione</SortableTableHead>
                  <SortableTableHead field="prezzo_vendita_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">
                    Prezzo
                  </SortableTableHead>
                  <SortableTableHead field="costo_totale_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">Costo</SortableTableHead>
                  <SortableTableHead field="margine_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">Margine</SortableTableHead>
                  <SortableTableHead field="num_prodotti" currentOrdering={ordering} onSort={setOrdering} className="text-center">Prodotti</SortableTableHead>
                  <SortableTableHead field="giacenza_disponibile" currentOrdering={ordering} onSort={setOrdering} className="text-center">Disp</SortableTableHead>
                  <SortableTableHead field="giacenza_riservata" currentOrdering={ordering} onSort={setOrdering} className="text-center">Ris</SortableTableHead>
                  <SortableTableHead field="giacenza_effettiva" currentOrdering={ordering} onSort={setOrdering} className="text-center">Eff</SortableTableHead>
                  <SortableTableHead field="stock_status" currentOrdering={ordering} onSort={setOrdering} className="text-center">Stock</SortableTableHead>
                  <SortableTableHead field="attivo" currentOrdering={ordering} onSort={setOrdering} className="text-center">Stato</SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tipologieFiltrate.map((box) => {
                  const giacenza = giacenzeByTipologia.get(box.id)
                  return (
                    <TableRow key={box.id}>
                    <TableCell>
                      {box.immagine_url ? (
                        <img
                          src={box.immagine_url}
                          alt={box.nome}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{box.codice}</TableCell>
                    <TableCell className="font-medium">{box.nome}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {box.configurazione_nome}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(box.prezzo_vendita)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatCurrency(box.costo_totale)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end">
                        <span className={box.margine >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(box.margine)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {box.margine_percentuale}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{box.num_prodotti}</Badge>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {giacenza ? giacenza.quantita_disponibile : '-'}
                    </TableCell>
                    <TableCell className="text-center text-muted-foreground">
                      {giacenza ? giacenza.quantita_riservata : '-'}
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      {giacenza ? (giacenza.quantita_effettiva ?? (giacenza.quantita_disponibile - giacenza.quantita_riservata)) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {getStockBadge(giacenza)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {box.attivo ? (
                          <Badge variant="success">Attiva</Badge>
                        ) : (
                          <Badge variant="secondary">Inattiva</Badge>
                        )}
                        {box.disponibile_ecommerce && (
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            E-com
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Modifica box" aria-label="Modifica box" asChild>
                          <Link to={`/box/tipologie/${box.id}/modifica`}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-emerald-600 hover:text-emerald-700"
                          title="Assembla questa box"
                          aria-label="Assembla questa box"
                          asChild
                        >
                          <Link to={`/box/tipologie/assembla?tipologia_box=${box.id}`}>
                            <Factory className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-amber-600 hover:text-amber-700"
                          onClick={() => openStockDialog(box)}
                          title="Modifica ubicazione"
                          aria-label="Modifica ubicazione"
                        >
                          <BuildingStorefrontIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-blue-600 hover:text-blue-700"
                          onClick={() => openDuplicaDialog(box)}
                          title="Duplica box"
                          aria-label="Duplica box"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          title="Elimina box"
                          aria-label="Elimina box"
                          onClick={() => confirmDelete(box)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, box: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la box &quot;{deleteDialog.box?.nome}&quot;?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, box: null })}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Duplica Box */}
      <Dialog open={duplicaDialog.open} onOpenChange={(open) => !duplicaMutation.isPending && setDuplicaDialog({ open, box: null, nuovoCodice: '', nuovoNome: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplica Box</DialogTitle>
            <DialogDescription>
              Crea una copia di &quot;{duplicaDialog.box?.nome}&quot; con un nuovo codice.
              La box duplicata sarà inattiva e non disponibile su e-commerce.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nuovo-codice">Nuovo Codice *</Label>
              <Input
                id="nuovo-codice"
                value={duplicaDialog.nuovoCodice}
                onChange={(e) => setDuplicaDialog({ ...duplicaDialog, nuovoCodice: e.target.value })}
                placeholder="BOX-001-COPIA"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nuovo-nome">Nuovo Nome (opzionale)</Label>
              <Input
                id="nuovo-nome"
                value={duplicaDialog.nuovoNome}
                onChange={(e) => setDuplicaDialog({ ...duplicaDialog, nuovoNome: e.target.value })}
                placeholder="Lascia vuoto per aggiungere (Copia) al nome"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDuplicaDialog({ open: false, box: null, nuovoCodice: '', nuovoNome: '' })}
              disabled={duplicaMutation.isPending}
            >
              Annulla
            </Button>
            <Button 
              onClick={handleDuplica} 
              disabled={duplicaMutation.isPending || !duplicaDialog.nuovoCodice.trim()}
            >
              {duplicaMutation.isPending ? 'Duplicazione...' : 'Duplica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={stockDialog.open}
        onOpenChange={(open) => setStockDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ubicazione Box</DialogTitle>
            <DialogDescription>
              La quantità di {stockDialog.box?.nome} cambia solo tramite assemblaggio,
              carico o scarico merce.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              Quantità disponibile: <strong>{stockDialog.giacenza?.quantita_disponibile ?? 0}</strong>
            </div>
            <div className="space-y-1">
              <Label>Soglia minima</Label>
              <p className="text-sm text-muted-foreground">
                Non prevista per le giacenze box assemblate.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock-box-ubicazione">Ubicazione</Label>
              <Input
                id="stock-box-ubicazione"
                value={stockForm.ubicazione}
                onChange={(e) => setStockForm((prev) => ({ ...prev, ubicazione: e.target.value }))}
                placeholder="Ubicazione (opzionale)"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStockDialog({ open: false, box: null, giacenza: null })}
            >
              Annulla
            </Button>
            <Button onClick={handleStockSubmit} disabled={updateStockMutation.isPending}>
              {updateStockMutation.isPending ? 'Salvataggio...' : 'Salva Ubicazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ListPagination data={boxData?.data} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
